/** ADC Part 1 pillar + commercial pages.
 * Routes:
 *   /adc-part-1                - main pillar hub
 *   /adc-part-1/questions      - ADC MCQ question bank
 *   /adc-part-1/mock-exam      - full-length readiness trial / mock
 *   /adc-part-1/study-guide    - how to study (subjects, timeline, plan)
 *
 * These are additive to /guides/*. All existing guide pages remain and keep
 * their equity; this cluster gives Google a clear topical hub to attribute
 * "ADC Part 1 preparation" authority to.
 */
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import AnnouncementBar from "@/components/AnnouncementBar";
import { BrandLogo } from "@/components/BrandLogo";
import { trackEvent } from "@/lib/analytics";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Target, Clock, BookOpen,
  BarChart3, Sparkles, Timer, MessageCircle, Compass, ChevronRight,
} from "lucide-react";

const SITE = "https://passadc.com";
const PUBLISHED = "2026-02-17";
const MODIFIED = "2026-09-13";

// Weights = official ADC blueprint disciplines this subject maps to (ADC Written
// Examination Handbook, Rev E Sept 2025). See /api/public/adc-blueprint.
const SUBJECTS = [
  { slug: "adc-part-1-operative-dentistry", label: "Operative / Restorative Dentistry", weight: "Restorative 14%" },
  { slug: "adc-part-1-paediatric-dentistry", label: "Paediatric Dentistry", weight: "Paeds & ortho 10%" },
  { slug: "adc-part-1-orthodontics", label: "Orthodontics", weight: "Paeds & ortho 10%" },
  { slug: "adc-part-1-community-dentistry", label: "Community & Preventive Dentistry", weight: "Preventive 10% · Infection control 5%" },
  { slug: "adc-part-1-periodontics", label: "Periodontics", weight: "9%" },
  { slug: "adc-part-1-oral-medicine", label: "Oral Medicine & Pathology", weight: "Oral med & path 7% · General medicine 8%" },
  { slug: "adc-part-1-endodontics", label: "Endodontics", weight: "7% · Dental emergencies 8%" },
  { slug: "adc-part-1-oral-surgery", label: "Oral Surgery", weight: "7% · Pain & anxiety 5%" },
  { slug: "adc-part-1-prosthodontics", label: "Prosthodontics", weight: "Fixed 5% · Removable 5%" },
  { slug: "adc-part-1-pharmacology", label: "Pharmacology", weight: "Assessed across disciplines · 10%" },
];

function setMeta(selector, attr, value, cache) {
  const el = document.querySelector(selector);
  if (!el) return;
  cache[selector] = el.getAttribute(attr);
  el.setAttribute(attr, value);
}
function restoreMeta(selector, attr, cache) {
  const el = document.querySelector(selector);
  if (el && cache[selector] != null) el.setAttribute(attr, cache[selector]);
}

function useSeo({ slug, title, description, faqs, breadcrumbLabel }) {
  useEffect(() => {
    const cache = {};
    document.title = title;
    const canonical = `${SITE}${slug}`;
    setMeta('meta[name="description"]', "content", description, cache);
    setMeta('link[rel="canonical"]', "href", canonical, cache);
    setMeta('meta[property="og:title"]', "content", title.replace(" | PassADC", ""), cache);
    setMeta('meta[property="og:description"]', "content", description, cache);
    setMeta('meta[property="og:url"]', "content", canonical, cache);
    setMeta('meta[name="twitter:title"]', "content", title.replace(" | PassADC", ""), cache);
    setMeta('meta[name="twitter:description"]', "content", description, cache);

    const scripts = [];
    const article = document.createElement("script");
    article.type = "application/ld+json";
    article.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: title.replace(" | PassADC", ""),
      description,
      url: canonical,
      datePublished: PUBLISHED,
      dateModified: MODIFIED,
      author: { "@type": "Organization", name: "PassADC", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "PassADC",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/apple-touch-icon.png` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    });
    document.head.appendChild(article);
    scripts.push(article);

    const crumbs = document.createElement("script");
    crumbs.type = "application/ld+json";
    const items = [{ "@type": "ListItem", position: 1, name: "PassADC", item: SITE }];
    if (breadcrumbLabel && slug !== "/adc-part-1") {
      items.push({ "@type": "ListItem", position: 2, name: "ADC Part 1", item: `${SITE}/adc-part-1` });
      items.push({ "@type": "ListItem", position: 3, name: breadcrumbLabel, item: canonical });
    } else {
      items.push({ "@type": "ListItem", position: 2, name: "ADC Part 1", item: canonical });
    }
    crumbs.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items,
    });
    document.head.appendChild(crumbs);
    scripts.push(crumbs);

    if (faqs && faqs.length) {
      const faq = document.createElement("script");
      faq.type = "application/ld+json";
      faq.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map(([q, a]) => ({
          "@type": "Question", name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      });
      document.head.appendChild(faq);
      scripts.push(faq);
    }
    window.scrollTo(0, 0);
    return () => {
      document.title = "ADC Part 1 Exam Preparation, MCQs & Mock Exams | PassADC";
      restoreMeta('meta[name="description"]', "content", cache);
      restoreMeta('link[rel="canonical"]', "href", cache);
      restoreMeta('meta[property="og:title"]', "content", cache);
      restoreMeta('meta[property="og:description"]', "content", cache);
      restoreMeta('meta[property="og:url"]', "content", cache);
      restoreMeta('meta[name="twitter:title"]', "content", cache);
      restoreMeta('meta[name="twitter:description"]', "content", cache);
      scripts.forEach((s) => s.remove());
    };
  }, [slug, title, description, faqs, breadcrumbLabel]);
}

/* ---------- Shared shell ---------- */

const PageShell = ({ children, breadcrumb }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background relative">
      <AnnouncementBar context="public" />
      <header className="relative z-10 px-6 pt-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2">
            <BrandLogo size={24} />
            <span className="font-bold text-base">PassADC</span>
          </button>
          <Button
            variant="ghost" size="sm"
            className="rounded-full text-sm font-medium hover:bg-secondary"
            onClick={() => navigate("/login")}
            data-testid="pillar-nav-sign-in-btn"
          >
            Sign in
          </Button>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        {breadcrumb && (
          <nav className="text-xs text-muted-foreground mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/adc-part-1" className="hover:text-foreground">ADC Part 1</Link>
            {breadcrumb !== "ADC Part 1" && (
              <>
                <span className="mx-2">/</span>
                <span className="text-foreground">{breadcrumb}</span>
              </>
            )}
          </nav>
        )}
        <article className="max-w-none text-base leading-relaxed [&_h1]:text-3xl md:[&_h1]:text-4xl [&_h1]:font-bold [&_h1]:leading-tight [&_h1]:mt-2 [&_h1]:mb-5 [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-2 [&_p]:my-4 [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary/80 [&_table]:w-full [&_table]:my-6 [&_table]:text-sm [&_th]:text-left [&_th]:font-semibold [&_th]:border-b [&_th]:border-border [&_th]:py-2 [&_th]:pr-3 [&_td]:border-b [&_td]:border-border/60 [&_td]:py-2 [&_td]:pr-3">
          {children}
        </article>
      </div>
      <Footer />
    </div>
  );
};

const PillarCTA = ({ label = "Check My ADC Readiness Free", location }) => {
  const navigate = useNavigate();
  return (
    <div className="my-10 rounded-2xl border border-primary/25 bg-primary/5 p-6 text-center not-prose">
      <p className="font-semibold mb-1">Where do you actually stand?</p>
      <p className="text-sm text-muted-foreground mb-4">6 ADC-style questions. Free, instant, no credit card.</p>
      <Button
        className="rounded-full px-6"
        onClick={() => {
          trackEvent("readiness_cta_clicked", { location });
          navigate("/readiness-check");
        }}
        data-testid="pillar-cta-btn"
      >
        {label} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

const SubjectGrid = () => (
  <div className="not-prose grid sm:grid-cols-2 gap-3 my-8">
    {SUBJECTS.map((s) => (
      <Link
        key={s.slug}
        to={`/guides/${s.slug}`}
        className="block rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors"
        data-testid={`pillar-subject-${s.slug}`}
      >
        <div className="flex items-center justify-between">
          <span className="font-semibold">{s.label}</span>
          <span className="text-xs text-muted-foreground">{s.weight}</span>
        </div>
      </Link>
    ))}
  </div>
);

/* ---------- Page 1: /adc-part-1 (pillar) ---------- */

export const AdcPart1Pillar = () => {
  useSeo({
    slug: "/adc-part-1",
    title: "ADC Part 1 Question Bank, Mock Exams & Study Plan",
    description: "ADC-style MCQs with explanations, full mock exams and a readiness score across every ADC Part 1 discipline. Start free with 10 practice questions a day.",
    breadcrumbLabel: "ADC Part 1",
    faqs: [
      ["What is the ADC Part 1 exam?", "The ADC Part 1 is the written examination overseas-trained dentists must pass on the Australian Dental Council pathway. It is a computer-based, 280-question MCQ exam delivered over two consecutive days at Pearson VUE test centres worldwide."],
      ["How should I prepare for ADC Part 1?", "The framework that consistently works: diagnose your weak subjects across every discipline in the ADC blueprint, prioritise high-weight weak subjects, practise 200-400 scenario-based MCQs per week, and sit at least 2 full-length timed mocks before booking the exam."],
      ["How long does ADC Part 1 preparation take?", "Most working dentists need 8-12 weeks at 1.5-2 focused hours per day. Full-time preparation can compress this to 6-8 weeks. Unstructured longer study rarely beats targeted 10-week plans."],
      ["What is the ADC Part 1 pass rate?", "The ADC Annual Report 2024-25 reports an overall written-examination pass rate of 12% (404 of 3,325 candidates), down from 41% in 2023-24 (1,209 of 2,925). These are all-candidate figures, not first-attempt rates. The ADC does not publish a cause for the decline."],
      ["What does ADC Part 1 preparation cost?", "The ADC written examination fee is AUD $2,122 per attempt (adc.org.au). Preparation costs range from AUD $19/month (PassADC, self-paced) to US$1,050+ for cohort coaching."],
      ["Where can I sit the ADC Part 1 exam?", "The written exam runs at Pearson VUE centres worldwide - you don't need to travel to Australia for Part 1. Common centres include Chennai, Delhi, Mumbai, Karachi, Lahore, London, Dubai and Auckland."],
    ],
  });
  return (
    <PageShell breadcrumb="ADC Part 1">
      <h1>ADC Part 1 Preparation: Everything You Need in One Hub</h1>
      <p>
        The <strong>ADC Part 1</strong> is the written examination on the Australian Dental Council
        pathway for overseas-trained dentists. It's a 280-question, scenario-based MCQ exam (56 clinical scenarios × 5 questions) across every discipline in the ADC blueprint and four competency clusters, and
        preparing for it well is what separates the candidates who pass from the majority who don't.
      </p>
      <p>
        This hub is your complete <strong>ADC Part 1 preparation</strong> map - the exam format, all 10
        subjects, your question bank, mock exams, and how to build a study plan that actually works.
      </p>

      <PillarCTA location="pillar_top" />

      <h2>How PassADC prepares you for ADC Part 1</h2>
      <p>Every high-scoring candidate follows the same four-step loop:</p>
      <div className="not-prose grid sm:grid-cols-2 gap-3 my-6">
        {[
          { icon: Compass, title: "1. Diagnose", body: "Take a readiness check weighted to the official ADC blueprint. See exactly where you stand - not guesses." },
          { icon: Target, title: "2. Prioritise", body: "Focus on high-weight weak subjects first. Oral Surgery at 55% beats Community at 40%." },
          { icon: BookOpen, title: "3. Practise", body: "200-400 scenario-based MCQs per week. Every wrong answer explained the same day." },
          { icon: Timer, title: "4. Simulate", body: "2-3 full-length timed mocks before you book the exam. Never rehearse for the first time on exam day." },
        ].map((s) => (
          <div key={s.title} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                <s.icon className="w-4 h-4" />
              </div>
              <h3 className="text-sm font-semibold m-0">{s.title}</h3>
            </div>
            <p className="text-sm text-muted-foreground m-0">{s.body}</p>
          </div>
        ))}
      </div>

      <h2>Your ADC Part 1 preparation pages</h2>
      <div className="not-prose grid sm:grid-cols-2 gap-3 my-6">
        {[
          { to: "/adc-part-1/questions", icon: Target, title: "ADC Part 1 Question Bank", desc: "1,200+ exam-style MCQs with per-option explanations." },
          { to: "/adc-part-1/mock-exam", icon: Clock, title: "ADC Part 1 Mock Exam", desc: "Full 140-question timed readiness trials." },
          { to: "/adc-part-1/study-guide", icon: BookOpen, title: "ADC Part 1 Study Guide", desc: "8-week study plan, timeline and subject priority." },
          { to: "/guides/adc-part-1-cost", icon: BarChart3, title: "ADC Part 1 Cost Breakdown", desc: "Fees in AUD, USD, GBP, INR, PKR - full budget." },
          { to: "/guides/adc-part-1-pass-rate", icon: Sparkles, title: "ADC Part 1 Pass Rate", desc: "Official ADC results: 41% (2023-24) and 12% (2024-25)." },
          { to: "/guides/how-to-pass-adc-part-1", icon: CheckCircle2, title: "How to Pass ADC Part 1", desc: "The complete first-attempt playbook." },
        ].map((c) => (
          <Link key={c.to} to={c.to} className="block rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                <c.icon className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-semibold m-0">{c.title}</h3>
                <p className="text-xs text-muted-foreground m-0 mt-1">{c.desc}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <h2>ADC Part 1 subjects (blueprint & weighting)</h2>
      <p>Our subject guides cover all 10 ADC Part 1 subjects. The weighting shown is the official target % for the discipline(s) each guide covers (ADC handbook, Rev E 2025):</p>
      <SubjectGrid />
      <p>
        Full topic coverage, high-yield areas and classic exam traps are broken out in each subject
        guide above. Start with the highest-weight subject you scored weakest on in your readiness check.
      </p>

      <h2>Country-specific ADC Part 1 cost guides</h2>
      <p>
        <Link to="/guides/adc-exam-cost-india">India (₹)</Link> ·{" "}
        <Link to="/guides/adc-exam-cost-pakistan">Pakistan (PKR)</Link> ·{" "}
        <Link to="/guides/adc-part-1-cost">All currencies (AUD, USD, GBP, INR, PKR, AED)</Link>
      </p>

      <PillarCTA label="Start your free ADC readiness check" location="pillar_bottom" />

      <h2>Frequently asked questions</h2>
      <p><strong>Is the ADC Part 1 really that hard?</strong> The exam isn't tricky - it's wide. Every discipline in the ADC blueprint, 280 questions, and you must pass all four competency clusters. Candidates who prep with structure and full-length mocks consistently pass. Candidates who prep by re-reading textbooks or hoarding recalled files consistently don't.</p>
      <p><strong>Do I need a cohort course to pass?</strong> No. Cohort courses (~US$1,050+) work but so does self-paced structured prep (AUD $89/6 months) for a fraction of the cost. The <em>structure</em> is what matters - not the delivery format.</p>
      <p><strong>How many MCQs should I practise?</strong> 200-400 per week is the sweet spot for most candidates. More than that and retention drops without proper review; less and you don't get the scenario-recognition reps you need.</p>
      <p><strong>When should I book the exam?</strong> Only after your full-length timed mock scores are consistently at readiness level. Booking earlier because "the next window is closing" is the top reason AUD $2,122 gets burned on failed attempts.</p>
    </PageShell>
  );
};

/* ---------- Page 2: /adc-part-1/questions ---------- */

export const AdcPart1Questions = () => {
  useSeo({
    slug: "/adc-part-1/questions",
    title: "ADC Part 1 Question Bank - 1,200+ Exam-Style MCQs | PassADC",
    description: "Practise 1,200+ exam-style Part 1 MCQ questions with per-option explanations, adaptive weak-area targeting and instant AI tutor feedback. Free tier available.",
    breadcrumbLabel: "Question Bank",
    faqs: [
      ["How many practice questions does the ADC Part 1 question bank have?", "1,200+ original exam-style MCQs mapped to the 13 official disciplines, with per-option explanations on over 1,000 questions and adaptive weak-area targeting."],
      ["Are the ADC Part 1 practice questions realistic?", "Yes. Every question is scenario-based and blueprint-weighted like the real ADC exam - case vignettes with single-best-answer options, weighted to the official ADC discipline blueprint."],
      ["Can I practise ADC Part 1 MCQs for free?", "Yes. The free tier includes 10 practice MCQs per day, per-option explanations, and full performance tracking. Pro unlocks unlimited practice, adaptive targeting and unlimited AI tutor follow-ups."],
      ["Do the ADC practice questions come with explanations?", "Yes. Each MCQ has an explanation for the correct answer, and over 1,000 questions have per-option explanations that tell you why each distractor is wrong - the fastest way to build clinical reasoning."],
      ["How many questions should I practise daily for ADC Part 1?", "30-50 scenario-based MCQs per day, 5-6 days per week - around 200-400 per week total. Every wrong answer's explanation should be read the same day."],
    ],
  });
  return (
    <PageShell breadcrumb="Question Bank">
      <h1>ADC Part 1 Question Bank: Practise 1,200+ Exam-Style MCQs and Find Your Weak Areas</h1>
      <p>
        The <strong>ADC Part 1 question bank</strong> is where the exam is actually won. The written
        exam is 280 scenario-based MCQs across 13 official disciplines - the only way to be ready is to have
        practised hundreds of them, in the same format, with proper explanations.
      </p>
      <p>
        PassADC's question bank has <strong>1,200+ exam-style MCQs</strong> mapped to the
        13 official ADC disciplines and weighted to the current exam blueprint. Every question is original,
        scenario-based and checked against current Australian clinical guidance - never recalled exam content.
      </p>

      <PillarCTA label="Start practising free - 10 MCQs/day" location="questions_top" />

      <h2>What's inside the ADC Part 1 question bank</h2>
      <ul>
        <li><strong>1,200+ exam-style MCQs</strong> - single best answer, exactly like the real exam</li>
        <li><strong>Per-option explanations</strong> on 1,000+ questions - you learn why each distractor is wrong, not just why one is right</li>
        <li><strong>Blueprint-weighted</strong> - questions are distributed across subjects to match the ADC's actual weighting</li>
        <li><strong>Scenario-based</strong> - case vignettes with linked questions, matching the real exam's clinical-reasoning format</li>
        <li><strong>Adaptive targeting</strong> - after your readiness check, the engine feeds you questions from your weakest subjects first</li>
        <li><strong>Smart Tutor on every question</strong> - plain-English follow-up explanations, unlimited on Pro</li>
      </ul>

      <h2>Subjects covered (all 10)</h2>
      <SubjectGrid />

      <h2>How to use the ADC Part 1 question bank effectively</h2>
      <ol>
        <li><strong>Take the readiness check first.</strong> Don't grind through 1,200+ questions in order - start with your weakest high-weight subjects.</li>
        <li><strong>30-50 MCQs per day, 5-6 days per week.</strong> Consistent daily practice beats weekend cramming.</li>
        <li><strong>Read every wrong answer's explanation the same day.</strong> This is where the actual learning happens.</li>
        <li><strong>Flag questions for review.</strong> Your flagged deck is what you review in the final week.</li>
        <li><strong>Interleave subjects</strong> from week 4 onwards. Mixed-subject sessions build the pattern-recognition the real exam demands.</li>
      </ol>

      <h2>Free vs Pro question bank access</h2>
      <table className="not-prose w-full text-sm">
        <thead className="text-left border-b border-border">
          <tr><th className="py-2">Feature</th><th className="py-2">Free</th><th className="py-2">Pro</th></tr>
        </thead>
        <tbody>
          <tr className="border-b border-border/60"><td className="py-2">Daily MCQ cap</td><td>50 / day</td><td>Unlimited</td></tr>
          <tr className="border-b border-border/60"><td className="py-2">Per-option explanations</td><td>Full access</td><td>Full access</td></tr>
          <tr className="border-b border-border/60"><td className="py-2">Adaptive weak-area targeting</td><td>Limited</td><td>Full</td></tr>
          <tr className="border-b border-border/60"><td className="py-2">Smart Tutor follow-ups</td><td>Limited</td><td>Unlimited</td></tr>
          <tr><td className="py-2">Cost</td><td>$0</td><td>From $19/month</td></tr>
        </tbody>
      </table>

      <PillarCTA label="Try 6 free ADC-style questions now" location="questions_bottom" />

      <h2>Related ADC Part 1 resources</h2>
      <p>
        Take a full timed <Link to="/adc-part-1/mock-exam">mock exam</Link> to measure readiness, follow
        the <Link to="/adc-part-1/study-guide">study guide</Link> to plan the next 8 weeks, or return to
        the main <Link to="/adc-part-1">ADC Part 1 hub</Link>. Also see:{" "}
        <Link to="/guides/how-to-pass-adc-part-1">how to pass ADC Part 1</Link>,{" "}
        <Link to="/guides/adc-part-1-pass-rate">pass rate explained</Link>,{" "}
        <Link to="/guides/adc-part-1-cost">full cost breakdown</Link>.
      </p>
    </PageShell>
  );
};

/* ---------- Page 3: /adc-part-1/mock-exam ---------- */

export const AdcPart1MockExam = () => {
  useSeo({
    slug: "/adc-part-1/mock-exam",
    title: "ADC Part 1 Mock Exam - Full 140-Question Timed Readiness Trial | PassADC",
    description: "Sit a full-length ADC Part 1 mock exam: 140 timed questions across every discipline in the ADC blueprint, blueprint-weighted, with detailed per-subject readiness scoring. Start free with PassADC.",
    breadcrumbLabel: "Mock Exam",
    faqs: [
      ["What is an ADC Part 1 mock exam?", "A full-length simulated ADC written exam - 140 questions across every discipline in the ADC blueprint, timed to match the real 2-hour section format, blueprint-weighted so the subject mix matches the actual exam."],
      ["How long is the ADC Part 1 mock exam?", "The mock is 140 questions with a 4-hour time limit, matching the two 70-question sections that make up one day of the real ADC written exam."],
      ["Can I take an ADC Part 1 mock exam for free?", "Yes. The PassADC free tier includes 1 full-length 140-question Readiness Trial. Pro unlocks all 3 readiness trials plus unlimited timed mock exams."],
      ["How does the mock exam score me?", "You get an overall score plus per-subject accuracy percentages weighted to the ADC blueprint. This feeds into your Live Readiness Score and pass prediction so you know exactly what to fix before exam day."],
      ["How many mock exams should I sit before the ADC Part 1?", "At least 2 full-length timed mocks in the final 2-3 weeks before the exam, ideally 3. The first exposes your stamina and weak-area patterns; the second confirms readiness after targeted repair; the optional third is a final rehearsal 3-4 days out."],
    ],
  });
  return (
    <PageShell breadcrumb="Mock Exam">
      <h1>ADC Part 1 Mock Exam: Take a Full Readiness Trial</h1>
      <p>
        The <strong>ADC Part 1 mock exam</strong> - also called a <em>Readiness Trial</em> - is the
        single most important preparation step before booking the AUD $2,122 real exam. It's a
        full-length simulation that tests stamina, timing and subject readiness in the same conditions
        as exam day.
      </p>
      <p>
        Candidates who sit at least two full-length mocks pass first time far more often than those
        who don't. Full stop.
      </p>

      <PillarCTA label="Start your free Readiness Trial" location="mock_top" />

      <h2>What's inside a PassADC Readiness Trial</h2>
      <ul>
        <li><strong>140 questions</strong> - the same volume as one day of the real ADC written exam</li>
        <li><strong>Blueprint-weighted</strong> - subject distribution matches the real exam (~15% Oral Surgery, ~12% Perio, ~12% Prostho, etc.)</li>
        <li><strong>Timed, exam-day conditions</strong> - 4 hours total, no pausing, timed breaks only</li>
        <li><strong>Per-subject scoring</strong> - after the mock, you see accuracy per subject weighted to the blueprint</li>
        <li><strong>Feeds your Readiness Score</strong> - each trial updates your live pass-probability gauge</li>
        <li><strong>Full explanation review</strong> - every wrong answer gets a per-option explanation for post-mock review</li>
      </ul>

      <h2>Why full-length mocks matter more than most candidates realise</h2>
      <p>The ADC Part 1 written exam is a <strong>stamina event</strong>:</p>
      <ul>
        <li>140 questions in a single day</li>
        <li>Over 2 consecutive days (280 total)</li>
        <li>4 hours of focused clinical reasoning per day</li>
        <li>Scenario-based questions requiring interpretation, not recall</li>
      </ul>
      <p>
        If exam day is the first time you sit a full 4-hour timed session, you'll hit a cognitive wall
        in section 3 or 4. The wall is real. The only way to build the fitness is to <em>practise the
        fitness</em>.
      </p>

      <h2>How to use PassADC Readiness Trials in your prep</h2>
      <ol>
        <li><strong>Week 5-6 of your prep:</strong> Sit Trial 1. Treat it like the real day - same start time, no phones, timed breaks. Review every wrong answer over the following 2 days.</li>
        <li><strong>Week 7:</strong> Targeted repair on the subjects Trial 1 exposed as weak.</li>
        <li><strong>Week 8, 4-5 days before exam:</strong> Sit Trial 2. Your accuracy should be noticeably higher. If it's not, don't book - extend prep another 2-3 weeks.</li>
        <li><strong>Optional Trial 3, 2-3 days out:</strong> Confirmation rehearsal only. Full post-mortem not needed - the exam is imminent.</li>
      </ol>

      <h2>Free vs Pro mock exam access</h2>
      <table className="not-prose w-full text-sm">
        <thead className="text-left border-b border-border">
          <tr><th className="py-2">Feature</th><th className="py-2">Free</th><th className="py-2">Pro</th></tr>
        </thead>
        <tbody>
          <tr className="border-b border-border/60"><td className="py-2">Full-length Readiness Trials</td><td>1</td><td>3</td></tr>
          <tr className="border-b border-border/60"><td className="py-2">Per-subject scoring</td><td>Yes</td><td>Yes</td></tr>
          <tr className="border-b border-border/60"><td className="py-2">Live Readiness Score</td><td>Locked</td><td>Full</td></tr>
          <tr className="border-b border-border/60"><td className="py-2">Timed mock exams (shorter)</td><td>Limited</td><td>Unlimited</td></tr>
          <tr><td className="py-2">Cost</td><td>$0</td><td>From $19/month</td></tr>
        </tbody>
      </table>

      <PillarCTA label="Take your free ADC Readiness Snapshot first" location="mock_bottom" />

      <h2>Related resources</h2>
      <p>
        Prepare with the <Link to="/adc-part-1/questions">ADC Part 1 question bank</Link>, plan the
        weeks leading up to your mock with the <Link to="/adc-part-1/study-guide">study guide</Link>,
        or return to the <Link to="/adc-part-1">ADC Part 1 hub</Link>. Also see:{" "}
        <Link to="/guides/how-to-pass-adc-part-1">how to pass ADC Part 1</Link>{" "}
        · <Link to="/guides/adc-part-1-pass-rate">pass rate explained</Link>.
      </p>
    </PageShell>
  );
};

/* ---------- Page 4: /adc-part-1/study-guide ---------- */

export const AdcPart1StudyGuide = () => {
  useSeo({
    slug: "/adc-part-1/study-guide",
    title: "ADC Part 1 Study Guide - 8-Week Preparation Plan (2026) | PassADC",
    description: "Complete ADC Part 1 study guide for overseas-trained dentists: 8-week study plan, subject priority, weekly targets, timeline options and how to know when you're ready to book the exam.",
    breadcrumbLabel: "Study Guide",
    faqs: [
      ["How long does it take to prepare for the ADC Part 1?", "Most working dentists need 8-12 focused weeks at 1.5-2 hours per day. Full-time preparation can compress this to 6-8 weeks. Longer prep isn't better if it's unstructured."],
      ["What subjects should I study first for ADC Part 1?", "Start with your weakest high-weight subjects. Priority = subject weighting × your accuracy gap. Oral Surgery at 55% accuracy usually beats Community Dentistry at 40% because it carries far more of the marks."],
      ["What is the best ADC Part 1 study plan?", "The diagnose-prioritise-practise-simulate loop: (1) diagnose across every discipline in the ADC blueprint; (2) prioritise weak high-weight subjects; (3) 200-400 scenario MCQs per week with same-day explanation review; (4) 2 full-length timed mocks in the final 2-3 weeks."],
      ["How many hours per day should I study for ADC Part 1?", "1.5-2 focused hours per day, 5-6 days per week, is realistic for most working dentists. Consistent daily practice beats weekend cramming."],
      ["Can I pass ADC Part 1 without a cohort course?", "Yes. Structured self-paced platforms with diagnostics, MCQ practice, mocks and explanations deliver the same core prep at a fraction of cohort cost. The structure is what matters - not the delivery format."],
    ],
  });
  return (
    <PageShell breadcrumb="Study Guide">
      <h1>ADC Part 1 Study Guide: The Complete 8-Week Preparation Plan</h1>
      <p>
        Every candidate who passes the ADC Part 1 first time uses roughly the same framework -
        whether they realise it or not. This guide walks through the full 8-week plan, subject
        priority logic and how to know when you're actually ready to book the exam.
      </p>

      <PillarCTA label="Take the free ADC Readiness Snapshot" location="studyguide_top" />

      <h2>The 8-week ADC Part 1 study plan</h2>
      <p>
        This plan assumes <strong>1.5-2 focused hours per day</strong>, 5-6 days per week. It scales
        cleanly to 6 weeks (full-time prep) or 12+ weeks (lighter daily load).
      </p>

      <h3>Week 1 - Diagnose, don't guess</h3>
      <ul>
        <li>Take a balanced diagnostic across every discipline in the ADC blueprint</li>
        <li>Rank subjects by weighting × accuracy deficit</li>
        <li>Your top 3 weak high-weight subjects will consume ~50-60% of study time</li>
      </ul>

      <h3>Weeks 2-4 - Rebuild your weakest three subjects</h3>
      <ul>
        <li>Pre-test → learn → post-test loop on each lesson</li>
        <li>End every day with 20-30 mixed MCQs from priority subjects</li>
        <li>Review every wrong answer's explanation the same day</li>
      </ul>

      <h3>Weeks 5-6 - Broaden and interleave</h3>
      <ul>
        <li>Bring the remaining 7 subjects into rotation</li>
        <li>Interleave subjects within each practice session, not single-subject days</li>
        <li>Start spaced-repetition reviews of everything you got wrong earlier</li>
      </ul>

      <h3>Week 7 - First full simulation</h3>
      <ul>
        <li>Sit a full-length, timed, blueprint-weighted mock (140 questions, 4 hours)</li>
        <li>Real exam conditions - same start time, timed breaks, no pausing</li>
        <li>Spend 2 days reviewing every incorrect answer</li>
      </ul>

      <h3>Week 8 - Repair and rehearse</h3>
      <ul>
        <li>Target only the weak areas the mock exposed</li>
        <li>Second full simulation 3-4 days before the exam</li>
        <li>Final 48 hours: review flagged questions only - no new content</li>
      </ul>

      <h2>Subject priority: where the marks actually are</h2>
      <p>
        Every discipline in the official ADC blueprint appears in every written exam, but their weightings vary from 5% to 14%. A
        smart study plan spends time in proportion to marks-available × your accuracy gap:
      </p>
      <SubjectGrid />
      <p>
        Every subject has its own high-yield topics and classic traps - see the linked subject
        guides for topic-by-topic breakdowns.
      </p>

      <h2>How do you know when you're ready to book the exam?</h2>
      <p>Book only when <strong>all three</strong> of these are true:</p>
      <ul>
        <li>Consistent 65-70%+ accuracy on scenario-based practice MCQs</li>
        <li>At least 2 full-length timed mocks completed, with the second at readiness level</li>
        <li>Weak subjects have been actively repaired since the last mock - not just noticed</li>
      </ul>
      <p>
        If any one of these isn't true, extending prep by 2-3 weeks costs far less than a failed
        attempt (AUD $2,122). See our full <Link to="/guides/adc-part-1-cost">cost
        breakdown</Link> for the total financial picture.
      </p>

      <PillarCTA label="Check where you stand today" location="studyguide_bottom" />

      <h2>Related ADC Part 1 resources</h2>
      <p>
        <Link to="/adc-part-1/questions">ADC Part 1 question bank</Link> ·{" "}
        <Link to="/adc-part-1/mock-exam">Full mock exam</Link> ·{" "}
        <Link to="/guides/how-to-pass-adc-part-1">How to pass ADC Part 1</Link> ·{" "}
        <Link to="/guides/adc-part-1-pass-rate">Pass rate explained</Link> ·{" "}
        <Link to="/guides/adc-part-1-cost">Full cost breakdown</Link> ·{" "}
        <Link to="/adc-part-1">Back to ADC Part 1 hub</Link>
      </p>
    </PageShell>
  );
};
